import { useAdminMeta } from '@numistic/data'
import { useTranslation } from '@numistic/i18n'
import { Alert } from '@numistic/ui'
import { createRoute, Link } from '@tanstack/react-router'
import { z } from 'zod'

import { useErrorText } from '../lib/errors.js'
import { adminRoute } from '../routes/admin/layout.js'
import { FieldsTab } from './fields-tab.js'
import { EnumsTab, GradesTab, StatusesTab } from './lists-tabs.js'
import { TypesTab } from './types-tab.js'

const Tab = z.enum(['types', 'fields', 'enums', 'statuses', 'grades'])

/** Метаданные (docs/PLAN.md, этап 3): типы, поля, значения, статусы и шкалы грейдов. */
export const adminMetaRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/meta',
  validateSearch: z.object({
    tab: Tab.optional().catch(undefined),
    item: z.string().optional().catch(undefined),
  }),
  component: AdminMetaPage,
})

function AdminMetaPage() {
  const { t } = useTranslation()
  const errorText = useErrorText()
  const { tab = 'types', item } = adminMetaRoute.useSearch()
  const data = useAdminMeta()
  if (data.error) return <Alert variant="destructive">{errorText.request(data.error)}</Alert>
  return (
    <div className="grid gap-6">
      <p className="text-sm text-muted-foreground">{t('adminMeta.hint')}</p>
      <nav className="flex flex-wrap gap-2" aria-label={t('admin.metaTitle')}>
        {Tab.options.map((x) => (
          <Link
            key={x}
            to="/admin/meta"
            search={{ tab: x }}
            aria-current={x === tab ? 'page' : undefined}
            className="rounded-full border px-3 py-1 text-sm text-muted-foreground hover:text-foreground aria-[current=page]:border-primary aria-[current=page]:text-foreground"
          >
            {t(`adminMeta.tabs.${x}`)}
          </Link>
        ))}
      </nav>
      {data.data ? (
        tab === 'types' ? (
          <TypesTab data={data.data} selected={item} />
        ) : tab === 'fields' ? (
          <FieldsTab data={data.data} />
        ) : tab === 'enums' ? (
          <EnumsTab data={data.data} selected={item} />
        ) : tab === 'statuses' ? (
          <StatusesTab data={data.data} />
        ) : (
          <GradesTab data={data.data} />
        )
      ) : null}
    </div>
  )
}
